import { Table } from 'components/core-ui/Table'
import s from './ExternalArticlesReportsReport.modules.scss'
import { useEffect, useMemo, useState } from 'react'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { useNotification } from 'hooks'
import { getColumns, getFilters } from './helpers'
import { DownloadActionIcon, ExportActionIcon } from 'icons'
import {
  getCollectingCompanyOptions,
  getWasteCategoryOptions,
  getLegalEntitiesOptions,
  getLocationOptions,
  downloadDocument,
  bulkDownload,
  downloadReportCsv,
  getLocationTypesOptions
} from '../api'
import { BulkDownloadFilters, ExternalArticlesReportTableItem, TableState } from '../interfaces'
import { useImmer } from 'use-immer'
import { BulkDownloadModal } from '../common/BulkDownloadModal'
import { useQuery } from 'react-query'
import { AutoCompleteValue, Filter } from 'components/core-ui/Table/TableDefinitions'

const ExternalArticlesReportsReport = () => {
  const [tableItems, setTableItems] = useState<ExternalArticlesReportTableItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [rowDownloadId, setRowDownloadId] = useState<number>(0)
  const [isBulkDownloadModalOpen, setIsBulkDownloadModalOpen] = useState<boolean>(false)
  const [bulkDownloadFilters, setBulkDownloadFilters] = useImmer<BulkDownloadFilters>({
    locations: [],
    startDate: null,
    endDate: null
  })
  const [tableState, setTableState] = useState<TableState>({
    order: null,
    orderBy: null,
    tableFilters: null
  })
  const [liveTableFilters, setLiveTableFilters] = useState<Filter[]>([])

  const openBulkDownloadModal = (): void => setIsBulkDownloadModalOpen(true)
  const closeBulkDownloadModal = (): void => setIsBulkDownloadModalOpen(false)

  const { showNotification } = useNotification()

  const toolbarActions = useMemo(
    () => [
      {
        label: 'Export Excel',
        icon: <ExportActionIcon />,
        onClick: () => {
          downloadReportCsvQuery.refetch()
        }
      },
      {
        label: 'Download',
        icon: <DownloadActionIcon />,
        onClick: () => openBulkDownloadModal()
      }
    ],
    []
  )

  const { data: wasteCategoryOptions } = getWasteCategoryOptions(showNotification)

  const { data: companyOptions } = getCollectingCompanyOptions(showNotification)

  const { data: legalEntitiesOptions } = getLegalEntitiesOptions(showNotification)

  const { data: shopOptions } = getLocationOptions(showNotification)

  const { data: locationTypesOptions } = getLocationTypesOptions(showNotification)

  const { data: locationSubtypesOptions } = useQuery(
    ['subtypes-filter', liveTableFilters.find(filter => filter.name === 'subtype.type.id')?.value],
    async () => {
      const typeLiveFilterValue = (liveTableFilters.find(
        filter => filter.name === 'subtype.type.id'
      )?.value || null) as AutoCompleteValue

      if (!typeLiveFilterValue?.value) return []
      const result = await makeCoreGetRequest(
        `/waste-management-system/locations/subtypes?storeTypeId=${typeLiveFilterValue?.value}`
      )

      const resultOptions = result.map((item: string) => ({
        label: item,
        value: item
      }))

      return resultOptions || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea listei de sub-tipuri de locatii. ${formatErrorMessage(
            error
          )}`,
          type: 'error'
        })
      }
    }
  )

  const downloadQuery = downloadDocument(
    showNotification,
    'EXTERNAL_ARTICLE',
    rowDownloadId,
    'EXTERNAL_PV',
    () => setRowDownloadId(0)
  )

  const downloadReportCsvQuery = downloadReportCsv(
    showNotification,
    toTableGETUrl({
      url: '/waste-management-system/report/external-articles/csv',
      tableFilters: tableState.tableFilters
    })
  )

  const bulkDownloadQuery = bulkDownload(
    showNotification,
    bulkDownloadFilters.locations?.map(location => `${location.value}`).join(','),
    bulkDownloadFilters.startDate,
    bulkDownloadFilters.endDate,
    closeBulkDownloadModal
  )

  useEffect(() => {
    if (rowDownloadId) {
      downloadQuery.refetch()
    }
  }, [rowDownloadId])

  const columns = useMemo(() => getColumns(), [])

  const filters = useMemo(
    () =>
      getFilters(
        legalEntitiesOptions,
        shopOptions,
        companyOptions,
        wasteCategoryOptions,
        locationTypesOptions,
        locationSubtypesOptions
      ),
    [
      legalEntitiesOptions,
      shopOptions,
      companyOptions,
      wasteCategoryOptions,
      locationTypesOptions,
      locationSubtypesOptions
    ]
  )

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/waste-management-system/report/external-articles',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableState({
        order,
        orderBy,
        tableFilters
      })

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor pentru rapoartele de predare. ${formatErrorMessage(
          error
        )}`,
        type: 'error'
      })
    }
  }

  const downloadBulkDocuments = () => {
    bulkDownloadQuery.refetch()
  }

  return (
    <div className={s.mainContainer}>
      <BulkDownloadModal
        isModalOpen={isBulkDownloadModalOpen}
        closeModal={closeBulkDownloadModal}
        filters={bulkDownloadFilters}
        setFilters={setBulkDownloadFilters}
        downloadBulkDocuments={downloadBulkDocuments}
      />
      <Table
        hideCheckbox
        orderBy="date"
        order="desc"
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        shouldResetPage={shouldResetPage}
        setShouldResetPage={setShouldResetPage}
        rowActions={[
          {
            label: 'Download',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#33691E',
            desktopIcon: <DownloadActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => setRowDownloadId(rowId),
            showTooltip: true
          }
        ]}
        rowActionsWidth="80px"
        toolbarActions={toolbarActions}
        filters={filters}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        handleFilterChangeLive={liveFilters => setLiveTableFilters(liveFilters)}
      />
    </div>
  )
}

export default ExternalArticlesReportsReport
